<template>
  <div class="payroll-page">
    <div class="page-header">
      <h1>{{ t('Gestão de Folha de Pagamento') }}</h1>
      <div class="header-actions">
        <Button
          v-if="canCreatePayrollPeriod"
          :label="t('Novo Período')"
          icon="pi pi-plus"
          class="p-button-primary"
          data-testid="payroll-create-period-button"
          @click="handleCreateNewPeriod"
        />
      </div>
    </div>

    <box type="primary">
      <!-- Payroll Periods Table -->
      <PayrollTable
        ref="payrollTableRef"
        @edit="handleEditPeriod"
      />
    </box>

    <!-- CreatePeriodModal is centralized in ModalManager.vue and opened via eventBus -->
    <!-- PayrollModal is centralized in ModalManager.vue and opened via eventBus -->
    <!-- Note: ConfirmDialog is provided globally in App.vue - do not add duplicate here -->
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePayrollPermissions } from '@/composables/usePayrollPermissions';
import { eventBus, type EventCallback } from '@/eventBus';
import { log } from '@/utils/DebugLogger';

type Id = number | string;

interface PayrollPeriod {
  id: Id;
  franchise_id: Id;
}

interface PayrollSavedPayload {
  id?: Id;
  [key: string]: unknown;
}

const { t } = useI18n();
const { canCreatePayrollPeriod } = usePayrollPermissions();

const payrollTableRef = ref<{ loadPeriods: () => void } | null>(null);

/**
 * Refresh the payroll periods table
 */
const refreshTable = (): void => {
  log('PayrollPage', 'refreshTable', 'Refreshing table');
  if (payrollTableRef.value?.loadPeriods) {
    payrollTableRef.value.loadPeriods();
  }
};

/**
 * Handle creating a new period - opens the wizard
 */
const handleCreateNewPeriod = (): void => {
  log('PayrollPage', 'handleCreateNewPeriod', 'Opening create period wizard');
  eventBus.emit('modal.createPeriod.open', {});
};

/**
 * Handle editing a specific period
 * Pass only IDs - PayrollModal will fetch fresh data from API (single source of truth)
 */
const handleEditPeriod = (period: PayrollPeriod): void => {
  log('PayrollPage', 'handleEditPeriod', 'Editing period', { id: period.id, franchiseId: period.franchise_id });
  eventBus.emit('modal.payroll.open', {
    periodId: period.id,
    franchiseId: period.franchise_id
  });
};

/**
 * Handle payroll saved event
 */
const handlePayrollSaved: EventCallback<PayrollSavedPayload> = (savedPeriod): void => {
  log('PayrollPage', 'handlePayrollSaved', 'Payroll saved', savedPeriod);
  refreshTable();
  eventBus.emit('globalMessage.success', {
    message: t('Período de folha processado com sucesso'),
    duration: 3000
  });
};

/**
 * Handle period created event
 */
const handlePeriodCreated: EventCallback = (): void => {
  log('PayrollPage', 'handlePeriodCreated', 'Period created, refreshing table');
  refreshTable();
};

onMounted(() => {
  log('PayrollPage', 'onMounted', 'Page mounted, listening for events');
  eventBus.on('payroll.saved', handlePayrollSaved);
  eventBus.on('employeePayroll.periodCreated', handlePeriodCreated);
  eventBus.on('payroll-period.created', handlePeriodCreated);
});

onBeforeUnmount(() => {
  log('PayrollPage', 'onBeforeUnmount', 'Cleaning up event listeners');
  eventBus.off('payroll.saved', handlePayrollSaved);
  eventBus.off('employeePayroll.periodCreated', handlePeriodCreated);
  eventBus.off('payroll-period.created', handlePeriodCreated);
});
</script>

<style scoped>
.payroll-page {
  padding: 2rem;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.page-header h1 {
  margin: 0;
  font-size: 2rem;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}
</style>
